import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-scroll';
import { useTheme } from 'styled-components';

import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import SegmentConfig from '../../../_shared/constants/segmentConfig';
import ROUTES from '../../Routes/constants';
import { BidSource } from '../../../_shared/types/auction';
import { TradeContextName } from '../../../_shared/types/trade';
import useAnalytics, {
  getGenericBiddingListProperties,
  getVinBasicProperties,
  getVinLaunchProperties,
  getSellerProperties
} from '../../../_shared/hooks/useAnalytics';

import { StyledVdpNav, StyledBadge, VdpNavUnderline } from './Styles';
import { OmpCanvasId, VdpId } from '../../../_shared/constants/vdp';

const navItems = [
  {
    name: <FormattedMessage id="vdp.innerNav.overview" />,
    value: 'overview'
  },
  {
    name: <FormattedMessage id="vdp.innerNav.history" />,
    value: 'history'
  },
  {
    name: <FormattedMessage id="vdp.innerNav.condition" />,
    value: 'condition'
  },
  {
    name: <FormattedMessage id="vdp.innerNav.transportSeller" />,
    value: 'transport'
  },
  {
    name: <FormattedMessage id="vdp.innerNav.research" />,
    value: 'research'
  }
];
const scrollDelay = 350;
const defaultVdpOffsetTop = 146;
const defaultVdpWidth = 1146;
const defaultVdpOffsetHeight = 2491;

function getCurrentScrollTop(isOmp) {
  return isOmp
    ? document.getElementById(OmpCanvasId).scrollTop
    : window.scrollY;
}

function getVdpOffsetLeft() {
  return (
    document.getElementById(VdpId)?.offsetLeft ??
    (document.body.offsetWidth - defaultVdpWidth) / 2
  );
}

const VdpNav = ({ sticky, tradeDetails, tradeContext, isOmp = false }) => {
  const theme = useTheme();
  const [underline, setUnderline] = useState({
    x: 261.17187,
    width: 90.046875,
    section: 'history'
  });
  const [navItemDetails, setNavItemDetails] = useState(navItems);
  const [scrollTopValue, setScrollTopValue] = useState(0);
  const [active, setActive] = useState('overview');
  const [forceScroll, setForceScroll] = useState(null);
  const track = useAnalytics();
  const forceMiniVdp = theme.FORCE_MINI_VDP_OMP;
  const vdpElement = document.getElementById(VdpId);
  const vdpOffsetTop = vdpElement?.offsetTop ?? defaultVdpOffsetTop;
  const vdpOffsetHeight = vdpElement?.offsetHeight ?? defaultVdpOffsetHeight;
  const fullHeight = isOmp
    ? vdpOffsetHeight + vdpOffsetTop
    : document.body.offsetHeight;

  useEffect(() => {
    if (forceScroll) {
      setActive(forceScroll);
      setTimeout(() => {
        setForceScroll(null);
      }, scrollDelay + 100);
    }
  }, [forceScroll]);

  // Effect that invokes when user scrolls to set the appropriate nav item to 'active'
  useEffect(() => {
    if (!forceScroll) {
      const scrollTimeout = setTimeout(() => {
        navItemDetails.forEach((item, i) => {
          if (
            item.value === navItems[0].value &&
            item.y <= vdpOffsetTop &&
            item.y + item.height / 2 > vdpOffsetTop
          ) {
            // If you're near the top of the page, but not quite inside the first section, set 'active' to first section
            setActive(navItems[0].value);
          } else if (
            navItemDetails[0].y + navItemDetails[0].height <= vdpOffsetTop &&
            navItemDetails[1].y > vdpOffsetTop
          ) {
            // Otherwise, set it to the second section as long as it is in view
            setActive(navItems[1].value);
          } else if (
            window.innerHeight + getCurrentScrollTop(isOmp) >=
            fullHeight
          ) {
            // If you're at the bottom of the page, set 'active' to the last item
            setActive(navItems[navItems.length - 1].value);
          } else if (
            (scrollTopValue < getCurrentScrollTop(isOmp) &&
              item.y + item.height / 2 <= vdpOffsetTop &&
              item.y + item.height > 146) ||
            (scrollTopValue > getCurrentScrollTop(isOmp) &&
              item.y + item.height / 2 >= vdpOffsetTop &&
              item.y > vdpOffsetTop - item.height / 2 &&
              item.y <= vdpOffsetTop)
          ) {
            // If scrolling down and the bottom half of a section is at the bottom of the inner nav (Vdp OffsetTop)
            // OR
            // If scrolling up and the top half of a section is at the bottom of the inner nav
            // Then set 'active' to the NEXT section because it is in view
            if (
              navItemDetails[i + 1] &&
              navItemDetails[i + 1].y + navItemDetails[i + 1].height <
                window.innerHeight
            ) {
              // However, only change what is 'active' if the next item is fully in view
              setActive(navItemDetails[i + 1].value);
            } else {
              // If there is no next section, you're near the bottom so set the last item as 'active'
              setActive(item.value);
            }
          }
        });

        return () => {
          clearTimeout(scrollTimeout);
        };
      }, 50);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollTopValue, navItemDetails]);

  // Effect that invokes when the current active section has changed
  useEffect(() => {
    const activeElement = document.getElementById(`nav-${active}`);
    if (active && activeElement) {
      // Set current active nav item to 'active'...
      if (!activeElement.classList.contains('active')) {
        activeElement.classList.add('active');
      }

      navItemDetails.forEach(item => {
        if (!sticky) {
          // ...but ignore the overview section because the nav is hidden
          setUnderline({
            x: 261.17,
            width: 118.79,
            section: 'history'
          });
        } else if (item.value === active && active !== 'overview') {
          // ...and move the underline below it
          setUnderline({
            x: item.x - getVdpOffsetLeft(),
            width: item.width,
            section: item.value
          });
        } else {
          // Otherwise, remove its active state (helps deactive all other nav items)
          document
            .getElementById(`nav-${item.value}`)
            .classList.remove('active');
        }
      });
    }
  }, [active, navItemDetails, sticky]);

  // Get the coordinate and dimensions data of each section
  const getSectionCoords = useCallback(() => {
    const navItemsWithBound =
      navItems &&
      navItems.map(item => {
        if (item && item.value) {
          const itemVal = document?.getElementById(item.value);
          if (itemVal) {
            const { height, y } = itemVal?.getBoundingClientRect();
            const navItemVal = document?.getElementById(`nav-${item.value}`);
            if (navItemVal) {
              const { width, x } = navItemVal?.getBoundingClientRect();

              // height is the height of the actual section's height
              // width is the width of the nav item in the navbar
              // x is the x-coordinate of the nav item in the navbar
              // y is the y-coordinate of the actual section relative to the window
              return {
                ...item,
                height,
                width,
                x,
                y
              };
            }
          }
        }
        return {};
      });

    // Everytime the user scrolls, save the previous y-coordinate for comparison later to determine if scrolling up or down
    setScrollTopValue(getCurrentScrollTop(isOmp));

    if (navItemsWithBound.length) {
      setNavItemDetails(navItemsWithBound);
    }
  }, [isOmp]);

  // Listen in on the scroll event with a timeout for better performance
  useEffect(() => {
    const timeout = setTimeout(() => {
      window.addEventListener('scroll', getSectionCoords, { capture: true });
      window.addEventListener('resize', getSectionCoords);
    }, 10);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', getSectionCoords, { capture: true });
      window.removeEventListener('resize', getSectionCoords);
    };
  }, [getSectionCoords]);

  // Effect to correct the underline's position when transitioning between sticky states
  useEffect(() => {
    if (document.getElementById(`nav-${underline.section}`)) {
      const { width, x } = document
        .getElementById(`nav-${underline.section}`)
        .getBoundingClientRect();

      if (!sticky) {
        setUnderline({
          x: 261.17,
          width: 118.79,
          section: 'history'
        });
      } else {
        setUnderline({
          section: underline.section,
          x: x - getVdpOffsetLeft(),
          width
        });
      }
    }
  }, [underline.section, sticky]);

  const conditionDisclosureCount = useSelector(
    state => state?.vdp?.disclosureCount?.CONDITION,
    shallowEqual
  )?.count;

  const histDisclosureCount = useSelector(
    state => state?.vdp?.disclosureCount?.VEHICLEHISTORY,
    shallowEqual
  )?.count;

  const getLinkContainerId = () => {
    if (forceMiniVdp) {
      return theme.WATCHLIST_VDP_OMP
        ? 'vdp-wrapper-watchlist'
        : 'vdp-container';
    }
    return isOmp ? 'canvas-content' : null;
  };

  return (
    <StyledVdpNav sticky={sticky} isOmp={isOmp} id="vdp-stickyNav">
      <ul>
        {navItemDetails.map(item => {
          return (
            <li key={item.value} data-testid={`vdpInnerNav-${item.value}`}>
              <Link
                to={`${item.value}`}
                containerId={getLinkContainerId()}
                smooth
                duration={scrollDelay}
                offset={-160}
                id={`nav-${item.value}`}
                onClick={() => {
                  if (isOmp) {
                    track(SegmentConfig.names.vdpNavBarNavigation, {
                      ...getVinBasicProperties(tradeDetails),
                      ...getVinLaunchProperties(tradeDetails),
                      ...getGenericBiddingListProperties(tradeDetails),
                      ...getSellerProperties(tradeDetails),
                      section_value: TradeContextName[tradeContext],
                      vdp_nav_section: `${item.value}`
                    });
                  } else {
                    track(MixpanelConfig.names.vdpNavBarSelection, {
                      trade_id: tradeDetails?.id,
                      auction_id: tradeDetails?.auctionSummary?.id,
                      vdp_nav_section: `${item.value}`,
                      source: window.location.href.includes(ROUTES.BUYING)
                        ? BidSource.MINI_VDP
                        : BidSource.VDP_OVERLAY
                    });
                  }
                  setForceScroll(item.value);
                }}
                tabIndex={0}
              >
                {item.name}
                {item.value === 'condition' && conditionDisclosureCount > 0 && (
                  <StyledBadge>{conditionDisclosureCount}</StyledBadge>
                )}
                {item.value === 'history' && histDisclosureCount > 0 && (
                  <StyledBadge>{histDisclosureCount}</StyledBadge>
                )}
                {item.value === 'transport' &&
                  tradeDetails?.transportInfo?.disclosureCount > 0 && (
                    <StyledBadge>
                      {tradeDetails?.transportInfo?.disclosureCount}
                    </StyledBadge>
                  )}
              </Link>
            </li>
          );
        })}
        <VdpNavUnderline underline={underline} />
      </ul>
    </StyledVdpNav>
  );
};

export default VdpNav;
